import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '@/settings/settings';

/**
 * Continuous ambient transcript capture — distinct from `useVoice.ts`'s
 * push-to-talk recognition (single utterance, stops on `onend`). This hook
 * is for "leave the mic open and capture ongoing radio/verbal traffic":
 * `continuous: true` + `interimResults: true`, and auto-restarts recognition
 * whenever the browser ends the session on its own (most engines cut a
 * session after a period of silence or a network hiccup, even in continuous
 * mode) as long as the officer hasn't explicitly stopped listening.
 *
 * Local types for the Web Speech API's continuous-mode result shape (not in
 * all TS DOM lib versions, and richer than the single-result shape
 * `useVoice.ts` uses — kept separate rather than shared to avoid coupling
 * two independently-evolving capture modes to one type).
 */
interface AmbientRecognitionAlternative { readonly transcript: string }
interface AmbientRecognitionResult { readonly isFinal: boolean; readonly length: number; [i: number]: AmbientRecognitionAlternative }
interface AmbientRecognitionResultList { readonly length: number; [i: number]: AmbientRecognitionResult }
interface AmbientRecognitionEvent { readonly resultIndex: number; readonly results: AmbientRecognitionResultList }
interface AmbientRecognitionErrorEvent {
  readonly error: string;
}
interface AmbientRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((evt: AmbientRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((evt: AmbientRecognitionErrorEvent) => void) | null;
  start(): void;
  stop(): void;
}
type AmbientRecognitionCtor = new () => AmbientRecognitionInstance;

export interface TranscriptSegment {
  id: string;
  text: string;
  at: string;
}

export interface AmbientTranscriptHook {
  /** True when both the browser and the user's voice setting support this. */
  available: boolean;
  /** Currently capturing. */
  listening: boolean;
  /** Finalised transcript segments, oldest first. */
  segments: TranscriptSegment[];
  /** In-progress (not yet finalised) text, for a live "still speaking" display. */
  interimText: string;
  start: () => void;
  stop: () => void;
  /** Clear accumulated segments (does not stop listening). */
  clear: () => void;
  /**
   * Which engine is actually producing this transcript — shown to the
   * officer so "what is Duty hearing this through" is never silently
   * ambiguous (the same provenance discipline as everywhere else facts are
   * displayed).
   */
  source: 'whisper' | 'web-speech';
  /**
   * Last capture failure the officer should actually see (mic permission
   * denied, no supported engine, an upload failing) — previously several of
   * these failed silently with no visible signal at all.
   */
  error?: string;
}

let segmentCounter = 0;
const newSegmentId = () => `seg_${Date.now().toString(36)}_${(segmentCounter++).toString(36)}`;

/**
 * Errors that mean "this environment fundamentally can't do this right now"
 * — surfaced to the officer and NOT auto-restarted (looping onerror→start on
 * a permission denial just burns the mic-permission prompt repeatedly).
 * Everything else (network hiccup, a silence timeout) is transient and
 * restarts transparently, same as before.
 */
const FATAL_RECOGNITION_ERRORS: Record<string, string> = {
  'not-allowed': 'Microphone permission denied — allow mic access in the browser to use voice capture.',
  'service-not-allowed': 'Microphone permission denied — allow mic access in the browser to use voice capture.',
  'audio-capture': 'No microphone found.',
};

export function useAmbientTranscript(): AmbientTranscriptHook {
  const { settings } = useSettings();

  const recognitionSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  const available = settings.voice && recognitionSupported;

  const [listening, setListening] = useState(false);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const recoRef = useRef<AmbientRecognitionInstance | null>(null);
  // Distinguishes "officer pressed stop" from "the browser ended the session on its own" —
  // only the latter should trigger an auto-restart.
  const wantListeningRef = useRef(false);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    if (recoRef.current) {
      try {
        recoRef.current.stop();
      } catch {
        /* ignore if already stopped */
      }
      recoRef.current = null;
    }
    setListening(false);
    setInterimText('');
  }, []);

  const start = useCallback(() => {
    if (!recognitionSupported || !settings.voice) return;
    if (recoRef.current) return; // already running
    wantListeningRef.current = true;
    setError(undefined);

    const w = window as unknown as Record<string, unknown>;
    const SR = (w['SpeechRecognition'] ?? w['webkitSpeechRecognition']) as AmbientRecognitionCtor | undefined;
    if (!SR) {
      setError('This browser has no speech recognition support (try Chrome or Edge).');
      return;
    }

    const reco = new SR();
    reco.continuous = true;
    reco.interimResults = true;
    reco.lang = 'en-AU';

    reco.onresult = (evt: AmbientRecognitionEvent) => {
      let interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const result = evt.results[i];
        const alt = result?.[0];
        if (!alt) continue;
        if (result.isFinal) {
          const text = alt.transcript.trim();
          if (text) {
            setSegments((prev) => [...prev, { id: newSegmentId(), text, at: new Date().toISOString() }]);
          }
        } else {
          interim += alt.transcript;
        }
      }
      setInterimText(interim.trim());
    };

    reco.onend = () => {
      recoRef.current = null;
      // The browser ends a continuous session on its own periodically (silence, network
      // hiccup) — restart transparently as long as the officer hasn't explicitly stopped.
      if (wantListeningRef.current) {
        start();
      } else {
        setListening(false);
        setInterimText('');
      }
    };

    reco.onerror = (evt) => {
      recoRef.current = null;
      const fatalMessage = FATAL_RECOGNITION_ERRORS[evt.error];
      if (fatalMessage) {
        wantListeningRef.current = false;
        setError(fatalMessage);
        setListening(false);
        return;
      }
      if (wantListeningRef.current) {
        start();
      } else {
        setListening(false);
      }
    };

    recoRef.current = reco;
    reco.start();
    setListening(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recognitionSupported, settings.voice]);

  const clear = useCallback(() => {
    setSegments([]);
    setInterimText('');
  }, []);

  // Stop everything when voice is turned off mid-session.
  useEffect(() => {
    if (!settings.voice) stop();
  }, [settings.voice, stop]);

  // Stop on unmount so a closed panel doesn't leave the mic open.
  useEffect(() => () => stop(), [stop]);

  return { available, listening, segments, interimText, start, stop, clear, source: 'web-speech', error };
}
